/**
 * File management utilities
 */

import {
  IconFile,
  IconPhoto,
  IconVideo,
  IconMusic,
  IconFileText,
  IconFileSpreadsheet,
  IconFileZip,
  IconFileTypePdf,
  IconFileCode,
  IconFileDescription,
} from '@tabler/icons-react';
import type { Icon } from '@tabler/icons-react';

/**
 * Get icon component for file MIME type
 */
export function getFileIcon(mimeType: string): Icon {
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType.startsWith('video/')) return IconVideo;
  if (mimeType.startsWith('audio/')) return IconMusic;
  if (mimeType.includes('pdf')) return IconFileTypePdf;
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    mimeType.includes('csv')
  ) {
    return IconFileSpreadsheet;
  }
  if (mimeType.includes('zip') || mimeType.includes('compressed') || mimeType.includes('archive')) {
    return IconFileZip;
  }
  if (
    mimeType.includes('text') ||
    mimeType.includes('document') ||
    mimeType.includes('word')
  ) {
    return IconFileText;
  }
  if (
    mimeType.includes('code') ||
    mimeType.includes('javascript') ||
    mimeType.includes('typescript') ||
    mimeType.includes('json') ||
    mimeType.includes('xml')
  ) {
    return IconFileCode;
  }
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
    return IconFileDescription;
  }

  return IconFile;
}

/**
 * Get color for file MIME type (returns CSS variable)
 */
export function getFileMimeColor(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'var(--mantine-color-green-5)';
  if (mimeType.startsWith('video/')) return 'var(--mantine-color-red-5)';
  if (mimeType.startsWith('audio/')) return 'var(--mantine-color-violet-5)';
  if (mimeType.includes('pdf')) return 'var(--mantine-color-red-6)';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return 'var(--mantine-color-green-6)';
  }
  if (mimeType.includes('zip') || mimeType.includes('compressed')) {
    return 'var(--mantine-color-orange-5)';
  }
  if (mimeType.includes('text') || mimeType.includes('document')) {
    return 'var(--mantine-color-blue-5)';
  }
  if (mimeType.includes('code')) {
    return 'var(--mantine-color-cyan-5)';
  }

  return 'var(--mantine-color-gray-5)';
}

/**
 * Get file extension from filename or URL (strips query params first)
 */
export function getFileExtension(fileName: string): string {
  const clean = fileName.split('?')[0].split('#')[0];
  const parts = clean.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

/**
 * Comprehensive MIME type map from file extension
 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  // Images
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp', ico: 'image/x-icon',
  avif: 'image/avif', tiff: 'image/tiff', tif: 'image/tiff',
  // Video
  mp4: 'video/mp4', webm: 'video/webm', ogg: 'video/ogg', ogv: 'video/ogg',
  mov: 'video/quicktime', avi: 'video/x-msvideo', mkv: 'video/x-matroska',
  m4v: 'video/x-m4v', '3gp': 'video/3gpp',
  // Audio
  mp3: 'audio/mpeg', wav: 'audio/wav', oga: 'audio/ogg', flac: 'audio/flac',
  aac: 'audio/aac', m4a: 'audio/mp4', wma: 'audio/x-ms-wma', opus: 'audio/opus',
  // PDF
  pdf: 'application/pdf',
  // Documents
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
  // Archives
  zip: 'application/zip', rar: 'application/x-rar-compressed',
  '7z': 'application/x-7z-compressed', tar: 'application/x-tar',
  gz: 'application/gzip', bz2: 'application/x-bzip2',
  // Text/Code
  txt: 'text/plain', csv: 'text/csv', json: 'application/json',
  xml: 'application/xml', html: 'text/html', css: 'text/css',
  js: 'text/javascript', ts: 'text/typescript', md: 'text/markdown',
  yml: 'text/yaml', yaml: 'text/yaml', log: 'text/plain',
};

/**
 * Infer the correct MIME type when the provided one is generic/missing.
 * Falls back to the original mimeType if no better match is found.
 */
export function inferMimeType(mimeType: string, fileName: string): string {
  if (
    mimeType &&
    mimeType !== 'application/octet-stream' &&
    mimeType !== 'binary/octet-stream'
  ) {
    return mimeType;
  }
  const ext = getFileExtension(fileName);
  return EXTENSION_MIME_MAP[ext] || mimeType || 'application/octet-stream';
}

/**
 * Image file extensions supported by modern browsers
 */
const IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif',
  'apng', 'tiff', 'tif', 'jfif', 'pjpeg', 'pjp', 'cur',
];

/**
 * Check if file is an image (by MIME type or file extension)
 */
export function isImageFile(mimeType: string, fileName?: string): boolean {
  if (mimeType.startsWith('image/')) return true;
  if (fileName) {
    const ext = getFileExtension(fileName);
    return IMAGE_EXTENSIONS.includes(ext);
  }
  return false;
}

/**
 * Video file extensions supported by modern browsers
 */
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'avi', 'mkv', 'm4v', '3gp'];

/**
 * Check if file is a video (by MIME type or file extension)
 */
export function isVideoFile(mimeType: string, fileName?: string): boolean {
  if (mimeType.startsWith('video/')) return true;
  if (fileName) {
    const ext = getFileExtension(fileName);
    return VIDEO_EXTENSIONS.includes(ext);
  }
  return false;
}

/**
 * Audio file extensions supported by modern browsers
 */
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'flac', 'aac', 'm4a', 'wma', 'opus', 'weba'];

/**
 * Check if file is audio (by MIME type or file extension)
 */
export function isAudioFile(mimeType: string, fileName?: string): boolean {
  if (mimeType.startsWith('audio/')) return true;
  if (fileName) {
    const ext = getFileExtension(fileName);
    return AUDIO_EXTENSIONS.includes(ext);
  }
  return false;
}

/**
 * Check if file is a PDF (by MIME type or file extension)
 */
export function isPdfFile(mimeType: string, fileName?: string): boolean {
  if (mimeType === 'application/pdf') return true;
  if (fileName) {
    return getFileExtension(fileName) === 'pdf';
  }
  return false;
}

/**
 * Check if file can be previewed in browser
 */
export function canPreviewInBrowser(mimeType: string): boolean {
  return (
    isImageFile(mimeType) ||
    isVideoFile(mimeType) ||
    isAudioFile(mimeType) ||
    isPdfFile(mimeType) ||
    mimeType === 'text/plain'
  );
}

/**
 * Determine if multipart upload should be used based on file size
 * Returns true if file is larger than 100MB
 */
export function shouldUseMultipartUpload(fileSize: number): boolean {
  const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB
  return fileSize > MULTIPART_THRESHOLD;
}

/**
 * Calculate optimal part size for multipart upload
 * AWS S3 allows 5MB to 5GB per part, we'll use 5MB-10MB range
 */
export function calculatePartSize(fileSize: number): number {
  const MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_PART_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_PARTS = 10000; // AWS S3 limit

  // Start with min part size
  let partSize = MIN_PART_SIZE;

  // If file would exceed max parts, increase part size
  while (Math.ceil(fileSize / partSize) > MAX_PARTS && partSize < MAX_PART_SIZE) {
    partSize *= 2;
  }

  return Math.min(partSize, MAX_PART_SIZE);
}

/**
 * Check if file is a text/code file
 */
export function isTextFile(mimeType: string, fileName: string): boolean {
  if (mimeType.startsWith('text/')) return true;
  if (mimeType.includes('json') || mimeType.includes('xml')) return true;
  const ext = getFileExtension(fileName);
  const textExtensions = [
    'txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'ts', 'tsx', 'jsx',
    'md', 'yml', 'yaml', 'log', 'ini', 'cfg', 'env', 'sh', 'py', 'rb',
    'go', 'rs', 'java', 'kt', 'swift', 'c', 'cpp', 'h', 'sql', 'graphql',
  ];
  return textExtensions.includes(ext);
}

/**
 * Document file extensions
 */
const DOCUMENT_EXTENSIONS = [
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp',
];

/**
 * Check if file is a document (Word, PowerPoint, Excel)
 */
export function isDocumentFile(mimeType: string, fileName?: string): boolean {
  if (
    mimeType.includes('document') ||
    mimeType.includes('word') ||
    mimeType.includes('presentation') ||
    mimeType.includes('powerpoint') ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel') ||
    mimeType.includes('opendocument')
  ) {
    return true;
  }
  if (fileName) {
    const ext = getFileExtension(fileName);
    return DOCUMENT_EXTENSIONS.includes(ext);
  }
  return false;
}

/**
 * Archive file extensions
 */
const ARCHIVE_EXTENSIONS = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'];

/**
 * Check if file is an archive
 */
export function isArchiveFile(mimeType: string, fileName?: string): boolean {
  if (
    mimeType.includes('zip') ||
    mimeType.includes('compressed') ||
    mimeType.includes('archive') ||
    mimeType.includes('tar') ||
    mimeType.includes('rar') ||
    mimeType.includes('7z') ||
    mimeType.includes('gzip')
  ) {
    return true;
  }
  if (fileName) {
    const ext = getFileExtension(fileName);
    return ARCHIVE_EXTENSIONS.includes(ext);
  }
  return false;
}

/** Regex patterns for video embed detection */
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
const VIMEO_REGEX = /(?:vimeo\.com\/)(\d+)/;

/**
 * Detect if a URL is a YouTube or Vimeo video link
 */
export function isVideoEmbedUrl(url: string): boolean {
  return YOUTUBE_REGEX.test(url) || VIMEO_REGEX.test(url);
}

/**
 * Convert a YouTube/Vimeo URL to an embeddable iframe URL
 */
export function getEmbedUrl(url: string): string | null {
  const youtubeMatch = url.match(YOUTUBE_REGEX);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  const vimeoMatch = url.match(VIMEO_REGEX);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  return null;
}

/**
 * Determine the file category for renderer dispatch.
 * Uses inferMimeType to correct generic MIME types before categorising.
 */
export function getFileCategory(file: { mimeType: string; fileName: string; externalUrl?: string }): import('./types').FileCategory {
  const { externalUrl, fileName } = file;
  const mimeType = inferMimeType(file.mimeType, fileName);

  if (externalUrl && isVideoEmbedUrl(externalUrl)) return 'video-embed';
  if (isImageFile(mimeType, fileName)) return 'image';
  if (isVideoFile(mimeType, fileName)) return 'video';
  if (isAudioFile(mimeType, fileName)) return 'audio';
  if (isPdfFile(mimeType, fileName)) return 'pdf';
  // Check documents/archives BEFORE text — prevents .docx (which is a zip
  // of XML files) from matching isTextFile via the XML mime type.
  if (isDocumentFile(mimeType, fileName)) return 'document';
  if (isArchiveFile(mimeType, fileName)) return 'archive';
  if (isTextFile(mimeType, fileName)) return 'text';

  return 'unknown';
}

/**
 * Get human-readable file type name
 */
export function getFileTypeName(mimeType: string): string {
  if (isImageFile(mimeType)) return 'Image';
  if (isVideoFile(mimeType)) return 'Video';
  if (isAudioFile(mimeType)) return 'Audio';
  if (isPdfFile(mimeType)) return 'PDF Document';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Spreadsheet';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'Document';
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'Presentation';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'Archive';
  if (mimeType.includes('text')) return 'Text File';

  return 'File';
}
